import React from 'react';
import styled from '@emotion/styled';
import {
  useFileIntelligenceSetting,
  useUpdateFileIntelligenceSetting,
} from 'hooks/file-intelligence-settings-hooks';

const Info = styled.div`
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  font-size: 0.95em;
  line-height: normal;
`;

export default function FileIntelligenceSetting() {
  const { data, isLoading, isError, isFetching, refetch } =
    useFileIntelligenceSetting();
  const updateMutation = useUpdateFileIntelligenceSetting();

  // A 404 already resolves as enabled in the query. Any other failure leaves
  // the value unknown unless a previous load succeeded, in which case React
  // Query keeps that last known value.
  const isKnown = data !== undefined;
  const isEnabled = data === true;

  // Never let a save and a read overlap: a read that lands after the save
  // could show the old value. The toggle waits for any read, and Retry waits
  // for any save.
  const isSaving = updateMutation.isLoading;

  // Never write a guess, and never let a read and a save overlap.
  const isToggleDisabled = !isKnown || isFetching || isSaving;

  const handleToggle = () => {
    // The handler enforces the disabled state for any event that still
    // reaches it.
    if (isToggleDisabled) return;
    updateMutation.mutate(!isEnabled);
  };

  return (
    <>
      {isLoading ? (
        <div
          id="loading"
          role="status"
          aria-label="Loading FileIntelligenceSetting"
        />
      ) : null}

      <div className="center-wrapper">
        <h2>File Intelligence</h2>
        <div style={{ color: '#7d7d7e' }}>
          Automatically analyze uploaded files to classify documents (type,
          year, and issuer), suggest file names and auto-rename client uploads,
          and suggest tags.
        </div>

        {isError && !isLoading ? (
          <div
            role="alert"
            className="d-flex align-items-center justify-content-between mt-4"
          >
            <span>
              {isKnown
                ? "Couldn't refresh the File Intelligence setting. Showing the last known value."
                : "Couldn't load the File Intelligence setting."}
            </span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm ml-2"
              onClick={() => refetch()}
              disabled={isFetching || isSaving}
            >
              Retry
            </button>
          </div>
        ) : null}

        <div className="mfa-card mt-4">
          <div className="d-flex align-items-center mb-3">
            <div className="toggle-container mr-2">
              <input
                type="checkbox"
                className="toggle"
                aria-label="Enable File Intelligence"
                checked={isEnabled}
                disabled={isToggleDisabled}
                onChange={handleToggle}
              />
            </div>

            <div className="mfa-title">Enable File Intelligence</div>

            {isEnabled && <div className="green-badge ml-auto">ENABLED</div>}
          </div>

          {isKnown && !isEnabled && (
            <Info style={{ backgroundColor: '#E6F7FB' }}>
              File Intelligence is turned off. New uploads won&apos;t be
              analyzed, classified, or renamed. Existing files are unchanged.
            </Info>
          )}

          {isEnabled && (
            <Info style={{ backgroundColor: '#F5F3B4' }}>
              File Intelligence is currently enabled for your firm. Disabling
              this setting stops analysis of new uploads; existing files are
              unchanged.
            </Info>
          )}

          {isEnabled && (
            <div
              style={{ marginTop: '15px', fontSize: '0.9em', color: '#666' }}
            >
              <strong>File Intelligence Includes:</strong>
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li>Document classification (type, year, issuer)</li>
                <li>File naming suggestions</li>
                <li>Auto-renaming of client uploads</li>
                <li>Tag suggestions</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
